import { DraftSparkle } from '@/assets/icons'
import Tooltip from '@/components/ui/tooltip/Tooltip'
import { useIsTruncated } from '@/components/ui/tooltip/useIsTruncated'
import { useIsTouchOnlyDevice } from '@/hooks/useIsTouchOnlyDevice'

export type DraftChipProps = {
  /** Draft title shown on the chip. */
  name: string
  /** Click handler — opens the draft (caller defines the action). */
  onClick: () => void
  /** Force the hover label open. Storybook/testing only. */
  forceTooltipOpen?: boolean
}

/**
 * DraftChip
 *
 * A compact chip for a single draft, shown on chat-history cards. The full name shows in a hover
 * label when the title is truncated.
 */
export function DraftChip({ name, onClick, forceTooltipOpen = false }: DraftChipProps) {
  const { ref, isTruncated } = useIsTruncated<HTMLSpanElement>()
  // On touch-only devices the tooltip toggles on tap and swallows it, which would block opening the draft.
  // Skip it and let the title truncate (the standard touch pattern).
  const isTouchOnly = useIsTouchOnlyDevice()

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Open draft: ${name}`}
      className="group flex w-fit max-w-full items-center gap-1.5 rounded border border-brand-neutral-200 bg-brand-neutral-50 px-2.5 py-1 text-left hover-capable:hover:border-brand-neutral-300 hover-capable:hover:bg-brand-neutral-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
    >
      <DraftSparkle className="size-[18px] shrink-0 text-brand-neutral-800 hover-capable:group-hover:text-brand-neutral-950" />
      <Tooltip
        title={!isTouchOnly && (isTruncated || forceTooltipOpen) ? name : ''}
        placement="top-start"
        offsetX={-5}
        offsetY={-10}
        forceOpen={forceTooltipOpen}
        disableInteractive
      >
        <span
          ref={ref}
          // One line via line-clamp-1 (not truncate). break-all fills the line before clipping.
          className="line-clamp-1 min-w-0 break-all text-xs font-medium leading-4 text-brand-neutral-800 hover-capable:group-hover:text-brand-neutral-950"
        >
          {name}
        </span>
      </Tooltip>
    </button>
  )
}
